import { util } from 'echarts/core';
import type StandardChart from './StandardChart';
import { type Theme, getTheme, parseThemeToken } from './theme';
import modelParsers from '../model';
import type { Option } from './option';

export type ThemeConfig = Theme['name'] | (Omit<Theme, 'name'> & { name?: Theme['name'] });

/**
 * 配置项管理
 */
export default class Model {
  protected _option: Option;

  protected _theme: Exclude<ThemeConfig, Theme['name']>;

  constructor(theme?: ThemeConfig) {
    this._option = {} as Option;

    if (typeof theme !== 'undefined' && typeof theme === 'string') {
      // eslint-disable-next-line no-param-reassign
      theme = {
        name: theme
      };
    }
    this._theme = theme;
  }

  // eslint-disable-next-line class-methods-use-this
  private __preprocess(option: Option) {
    ['xAxis', 'yAxis', 'series'].forEach(optionKey => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, no-param-reassign
      option[optionKey] = (
        Array.isArray(option[optionKey]) ? option[optionKey] : [option[optionKey]]
      ).filter(Boolean);
    });
  }

  private __parseTheme(
    option: Option,
    theme: Exclude<ThemeConfig, Theme['name']>,
    chart: StandardChart
  ) {
    if (typeof theme === 'undefined' || theme === null) {
      return this;
    }

    // * 利用 name 字段来判断是否获取预注册的主题配置
    const isNewTheme = typeof theme.name === 'undefined';
    const themeData = !isNewTheme ? getTheme(theme.name) : theme;
    const optionToken = parseThemeToken(themeData as Theme, isNewTheme ? {} : theme);

    (themeData?.parsers || []).forEach(themeParser => {
      themeParser(option, chart, optionToken);
    });

    return this;
  }

  private __parse(index: number, chart: StandardChart) {
    this.__preprocess(this._option);
    // * 解析主题配置
    this.__parseTheme(this._option, this._theme, chart);
    // * 解析配置
    modelParsers.forEach(modelParser => {
      modelParser.parse(this._option, index, chart);
    });
  }

  /**
   * 预解析配置项
   */
  prepare(chart: StandardChart) {
    this.__parse(0, chart);
  }

  getThemeName() {
    return this._theme?.name;
  }

  getOption() {
    return util.clone(this._option);
  }

  update(option: Option) {
    this._option = util.clone(option);
  }
}
